import {
    ManagedAgentRequestModel,
    ManagedAgentResponseModel,
    McpResponseModel,
    MgmtApi
} from '@/customApiClients/mgmtApiClient/Api';
import { cn } from '@/lib/utils';
import {
    Check,
    ChevronsUpDown,
    Loader2,
    Network,
    Pencil,
    Plus,
    Trash2,
    UploadCloud
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { FileRejection, useDropzone } from 'react-dropzone';
import { toast } from 'sonner';

import { ConfirmDeleteDialog } from '@/components/ConfirmDeleteDialog';
import { AutoConfirmControl, SkillToggleRows, ToggleRow, ToggleSection } from './ToggleRows';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList
} from '@/components/ui/command';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Popover,
    PopoverContent,
    PopoverTrigger
} from '@/components/ui/popover';
import { SectionHeader } from '@/components/ui/section-header';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from '@/components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from '@/components/ui/table';
import { StatusBadge } from '@/components/ui/status-badge';
import { TableSkeleton } from '@/components/ui/table-skeleton';
import { Textarea } from '@/components/ui/textarea';

import { useAuth } from '@/hooks/useAuth';

interface Props {
    mgmtApi: MgmtApi<unknown>;
}

// Same real Anthropic model ids/speeds used in AgentsTab.tsx — kept in sync
// manually since each tab has its own file (only "standard" | "fast" exist).
// Same built-in tool set/description used in AgentsTab.tsx — kept in sync
// manually since each tab has its own file.
const BUILTIN_TOOLS: { name: string; description: string }[] = [
    { name: 'bash', description: 'Run shell commands in the sandbox' },
    { name: 'edit', description: 'Modify existing files' },
    { name: 'read', description: 'Read files from the workspace' },
    { name: 'write', description: 'Create new files' },
    { name: 'glob', description: 'Find files by pattern' },
    { name: 'grep', description: 'Search file contents' },
    { name: 'web_fetch', description: 'Fetch a specific URL' },
    { name: 'web_search', description: 'Search the public web' }
];

interface ToolState {
    enabled: boolean;
    auto: boolean;
}

const makeEmptyTools = (): Record<string, ToolState> =>
    Object.fromEntries(
        BUILTIN_TOOLS.map((t) => [t.name, { enabled: false, auto: true }])
    );

// Fetched live from /api/models — same shape/source as AgentsTab.tsx, which
// this tab's model picker is kept in sync with (searchable dropdown rather
// than a fixed set of cards).
interface ModelItem {
    id: string;
    display_name: string;
    supports_effort?: boolean;
    effort_levels?: string[];
}

const EFFORT_LEVEL_LABELS: Record<string, string> = {
    low: 'Low',
    medium: 'Medium',
    high: 'High',
    xhigh: 'X-High',
    max: 'Max'
};

// One row = one ManagedAgent DB record tagged isCoordinator=true, enriched
// with live name/system/roster/version fetched using THAT record's own
// apiKey secret — mirrors the same DB-driven pattern used in AgentsTab.tsx.
interface CoordinatorRow {
    guid: string;
    agentId: string;
    displayName: string;
    organization: string;
    apiKey?: string | null;
    environmentId: string;
    vaultId?: string | null;
    liveName?: string | null;
    liveDescription?: string | null;
    liveSystem?: string | null;
    liveModelId?: string | null;
    liveModelSpeed?: string | null;
    liveModelEffort?: string | null;
    liveRoster?: string[];
    liveVersion?: number | null;
    liveTools?: Record<string, ToolState>;
    liveSkills?: string[];
    liveEnableDashboards?: boolean;
    isComplete: boolean; // has a non-empty roster
    loadError?: boolean;
}

interface SelectableResource {
    id: string;
    name: string;
    organization?: string | null;
}

interface SkillItem {
    skill_id: string;
    display_title: string;
    source: string;
}

/** Updates the Anthropic agent config, then upserts the mgmt DB pointer
 * record (isCoordinator=true) — the DB row is what the chat runtime and this
 * tab's own list actually rely on to find the coordinator, so both writes
 * are required on every save. */
interface CoordinatorForm {
    guid: string;
    agentId: string;
    name: string;
    description: string;
    system: string;
    modelId: string;
    modelSpeed: string;
    modelEffort: '' | 'low' | 'medium' | 'high' | 'xhigh' | 'max'; // '' = no override (agent default)
    organization: string;
    apiKeySecret: string;
    environmentId: string;
    vaultId: string; // '' = none selected
    roster: string[];
    enableDashboards: boolean;
    tools: Record<string, ToolState>;
    selectedSkills: string[];
}

const makeEmptyForm = (organization: string): CoordinatorForm => ({
    guid: '',
    agentId: '',
    name: '',
    description: '',
    system: '',
    modelId: '',
    modelSpeed: 'standard',
    modelEffort: '',
    organization,
    apiKeySecret: '',
    environmentId: '',
    vaultId: '',
    roster: [],
    enableDashboards: true,
    tools: makeEmptyTools(),
    selectedSkills: []
});

export const CoordinatorTab: React.FC<Props> = ({ mgmtApi }) => {
    const { user } = useAuth();
    const myOrganization = user?.metadata?.organization || '';

    // Coordinators — sourced from the mgmt DB (ManagedAgents where
    // isCoordinator = true), each row enriched with its own live Anthropic
    // config fetched using its own apiKey secret.
    const [coordinators, setCoordinators] = useState<CoordinatorRow[]>([]);
    const [skillCatalog, setSkillCatalog] = useState<SkillItem[]>([]);
    const [deletingSkillId, setDeletingSkillId] = useState<string | null>(null);
    const [skillToDelete, setSkillToDelete] = useState<string | null>(null);
    const [uploadingSkill, setUploadingSkill] = useState(false);
    const [models, setModels] = useState<ModelItem[]>([]);
    const [environments, setEnvironments] = useState<SelectableResource[]>([]);
    const [vaults, setVaults] = useState<SelectableResource[]>([]);
    const [mcpCatalog, setMcpCatalog] = useState<McpResponseModel[]>([]);

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [editingExisting, setEditingExisting] = useState(false);
    const [modelDropdownOpen, setModelDropdownOpen] = useState(false);
    const [form, setForm] = useState<CoordinatorForm>(() => makeEmptyForm(''));

    const fetchLiveConfig = async (
        row: ManagedAgentResponseModel
    ): Promise<Partial<CoordinatorRow>> => {
        const query = row.apiKey
            ? `?apiKeySecret=${encodeURIComponent(row.apiKey)}`
            : '';
        try {
            const res = await fetch(
                `/api/agents/${encodeURIComponent(row.agentId)}${query}`,
                { credentials: 'include' }
            );
            if (!res.ok) return { loadError: true };
            const c = await res.json();
            const tools = makeEmptyTools();
            for (const [toolName, state] of Object.entries<any>(
                c.builtinTools || {}
            )) {
                if (tools[toolName]) {
                    tools[toolName] = {
                        enabled: !!state.enabled,
                        auto: !!state.auto
                    };
                }
            }
            return {
                liveName: c.name,
                liveDescription: c.description,
                liveSystem: c.system,
                liveModelId: c.modelId,
                liveModelSpeed: c.modelSpeed,
                liveModelEffort: [
                    'low',
                    'medium',
                    'high',
                    'xhigh',
                    'max'
                ].includes(c.modelEffort)
                    ? c.modelEffort
                    : '',
                liveRoster: Array.isArray(c.agents) ? c.agents : [],
                liveVersion: c.version,
                liveTools: tools,
                liveEnableDashboards: c.enableDashboards !== false,
                liveSkills: Array.isArray(c.skills)
                    ? c.skills.map((s: { skill_id: string }) => s.skill_id)
                    : [],
                isComplete: Array.isArray(c.agents) && c.agents.length > 0
            };
        } catch {
            return { loadError: true };
        }
    };

    const fetchCoordinators = () => {
        setLoading(true);
        mgmtApi.api
            .v1ManagedAgentsList()
            .then(async (res) => {
                const all: ManagedAgentResponseModel[] = res?.data?.data || [];
                const coordRows = all.filter((a) => a.isCoordinator);
                const rows = await Promise.all(
                    coordRows.map(
                        async (row): Promise<CoordinatorRow> => ({
                            guid: row.guid,
                            agentId: row.agentId,
                            displayName: row.displayName,
                            organization: row.organization,
                            apiKey: row.apiKey,
                            environmentId: row.environmentId,
                            vaultId: row.vaultId,
                            isComplete: false,
                            ...(await fetchLiveConfig(row))
                        })
                    )
                );
                setCoordinators(rows);
            })
            .catch(() => {
                setCoordinators([]);
                toast.error('Failed to load coordinators');
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        fetchCoordinators();
        fetch('/api/models', { credentials: 'include' })
            .then((res) => (res.ok ? res.json() : []))
            .then((data) => setModels(Array.isArray(data) ? data : []))
            .catch(() => setModels([]));
        fetch('/api/environments', { credentials: 'include' })
            .then((res) => (res.ok ? res.json() : []))
            .then((data) => setEnvironments(Array.isArray(data) ? data : []))
            .catch(() => setEnvironments([]));
        fetch('/api/vaults', { credentials: 'include' })
            .then((res) => (res.ok ? res.json() : []))
            .then((data) => setVaults(Array.isArray(data) ? data : []))
            .catch(() => setVaults([]));
        fetchSkills();
        mgmtApi.api
            .v1McpsList()
            .then((res) => setMcpCatalog(res?.data?.data || []))
            .catch(() => setMcpCatalog([]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Default a brand-new coordinator to the first available model once the
    // list has loaded — editing an existing one overwrites this with its own
    // modelId shortly after (handleEditClick), same as AgentsTab.tsx.
    useEffect(() => {
        if (models.length === 0 || editingExisting) return;
        setForm((f) => (f.modelId ? f : { ...f, modelId: models[0].id }));
    }, [models, editingExisting]);

    // Only one coordinator is allowed per org (enforced server-side too) —
    // surface it here so the admin sees the conflict before hitting Save
    // instead of only getting a 400 back.
    const conflictingCoordinator = form.organization
        ? coordinators.find(
              (c) =>
                  c.organization === form.organization && c.guid !== form.guid
          )
        : undefined;

    // The caller's own org already has a coordinator — hide "New
    // Coordinator" entirely rather than let them find out via an error
    // after filling out the whole form.
    const myOrgCoordinator = myOrganization
        ? coordinators.find((c) => c.organization === myOrganization)
        : undefined;

    const setTool = (name: string, patch: Partial<ToolState>) =>
        setForm((f) => ({
            ...f,
            tools: { ...f.tools, [name]: { ...f.tools[name], ...patch } }
        }));

    // The admin can no longer edit the roster from this tab's UI, but a
    // coordinator's existing roster (set previously, e.g. via a direct API
    // call) is still loaded into form.roster by handleEditClick and must be
    // preserved as-is on every save — hence still sending it below.
    const cleanRoster = () => form.roster.map((r) => r.trim()).filter(Boolean);

    const resetForm = () => {
        setForm(makeEmptyForm(''));
        setShowForm(false);
    };

    const openNewForm = () => {
        setForm(makeEmptyForm(myOrganization));
        setEditingExisting(false);
        setShowForm(true);
    };

    const fetchSkills = () =>
        fetch('/api/skills', { credentials: 'include' })
            .then((res) => (res.ok ? res.json() : []))
            .then((data) => setSkillCatalog(Array.isArray(data) ? data : []))
            .catch(() => setSkillCatalog([]));

    const handleDeleteSkill = (skillId: string) => {
        setDeletingSkillId(skillId);
        fetch(`/api/skills/${encodeURIComponent(skillId)}`, {
            method: 'DELETE',
            credentials: 'include'
        })
            .then((res) => {
                if (!res.ok) throw new Error('Failed to delete skill');
                setForm((f) => ({
                    ...f,
                    selectedSkills: f.selectedSkills.filter(
                        (s) => s !== skillId
                    )
                }));
                return fetchSkills();
            })
            .then(() => toast.success('Skill deleted.'))
            .catch((e: any) =>
                toast.error(e?.message || 'Failed to delete skill')
            )
            .finally(() => setDeletingSkillId(null));
    };

    const handleSkillUpload = async (file: File) => {
        setUploadingSkill(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append(
                'displayTitle',
                file.name.replace(/\.(skill|zip)$/i, '')
            );
            const res = await fetch('/api/skills/upload', {
                method: 'POST',
                credentials: 'include',
                body: formData
            });
            if (!res.ok) {
                const j = await res.json().catch(() => null);
                throw new Error(j?.detail || 'Failed to upload skill');
            }
            const uploaded: { skill_id: string } = await res.json();
            await fetchSkills();
            setForm((f) => ({
                ...f,
                selectedSkills: [...f.selectedSkills, uploaded.skill_id]
            }));
            toast.success('Skill uploaded and selected');
        } catch (e: any) {
            toast.error(e?.message || 'Failed to upload skill');
        } finally {
            setUploadingSkill(false);
        }
    };

    const {
        getRootProps: getSkillDropRootProps,
        getInputProps: getSkillDropInputProps,
        isDragActive: isSkillDropActive
    } = useDropzone({
        onDrop: (accepted: File[], rejections: FileRejection[]) => {
            if (rejections.length > 0) {
                toast.error(
                    rejections[0].errors[0]?.message ||
                        'Only .skill or .zip files are accepted'
                );
                return;
            }
            if (accepted[0]) handleSkillUpload(accepted[0]);
        },
        multiple: false,
        disabled: uploadingSkill,
        accept: {
            'application/zip': ['.zip'],
            'application/octet-stream': ['.skill']
        }
    });

    const toggleSkill = (skillId: string) =>
        setForm((f) => ({
            ...f,
            selectedSkills: f.selectedSkills.includes(skillId)
                ? f.selectedSkills.filter((id) => id !== skillId)
                : [...f.selectedSkills, skillId]
        }));

    const skillsPayload = () =>
        form.selectedSkills.map((skillId) => ({
            skill_id: skillId,
            source:
                skillCatalog.find((s) => s.skill_id === skillId)?.source ??
                'anthropic'
        }));

    const handleEditClick = async (coord: CoordinatorRow) => {
        setForm({
            guid: coord.guid,
            agentId: coord.agentId,
            name: coord.liveName ?? coord.displayName,
            description: coord.liveDescription ?? '',
            system: coord.liveSystem ?? '',
            modelId: coord.liveModelId ?? '',
            modelSpeed: coord.liveModelSpeed ?? 'standard',
            modelEffort: (
                ['low', 'medium', 'high', 'xhigh', 'max'] as const
            ).includes(coord.liveModelEffort as any)
                ? (coord.liveModelEffort as CoordinatorForm['modelEffort'])
                : '',
            organization: coord.organization,
            apiKeySecret: coord.apiKey ?? '',
            environmentId: coord.environmentId ?? '',
            vaultId: coord.vaultId ?? '',
            roster: coord.liveRoster ?? [],
            enableDashboards: coord.liveEnableDashboards ?? true,
            tools: coord.liveTools ?? makeEmptyTools(),
            selectedSkills: coord.liveSkills ?? []
        });
        setEditingExisting(true);
        setShowForm(true);
    };

    const validate = (): boolean => {
        if (!form.name || !form.organization) {
            toast.error('Name and Organization are required');
            return false;
        }
        if (editingExisting) {
            if (!form.agentId || !form.agentId.startsWith('agent_')) {
                toast.error("Agent ID should start with 'agent_'");
                return false;
            }
        } else if (!form.modelId) {
            // Only required for a brand-new coordinator — agents.create has
            // no existing model to fall back to (editing an existing one
            // can leave modelId blank to keep its current model).
            toast.error('Model is required');
            return false;
        }
        if (!form.environmentId) {
            toast.error('Environment is required');
            return false;
        }
        if (!form.vaultId) {
            toast.error('Vault is required');
            return false;
        }
        if (conflictingCoordinator) {
            toast.error(
                `'${conflictingCoordinator.displayName}' is already the coordinator for '${form.organization}'. Remove or edit it instead.`
            );
            return false;
        }
        const bad = cleanRoster().filter((r) => !r.startsWith('agent_'));
        if (bad.length) {
            toast.error(`Roster entries must be agent ids: ${bad.join(', ')}`);
            return false;
        }
        return true;
    };

    // Extract a mgmt-API error's `detail` — the API throws the parsed
    // CustomProblemDetails body (not a JS Error), so `e.message` is always
    // undefined for these; this is what actually shows a useful message
    // (e.g. "Organization already has a coordinator") instead of a generic
    // fallback.
    const extractMgmtError = (e: any, fallback: string): string =>
        e?.error?.detail || e?.error?.Detail || e?.message || fallback;

    const handleSave = async () => {
        if (!validate()) return;
        setSaving(true);
        try {
            // 1. Get (or create) the Anthropic agent itself (name, model,
            // system, roster, coordinator tags). Editing an existing
            // coordinator updates it in place; a brand-new one is created
            // here — the admin never has to supply/paste an agent id.
            let resolvedAgentId = form.agentId;
            if (editingExisting) {
                const res = await fetch(
                    `/api/agents/${encodeURIComponent(form.agentId)}`,
                    {
                        method: 'PUT',
                        credentials: 'include',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            name: form.name,
                            modelId: form.modelId || null,
                            modelSpeed: form.modelSpeed || null,
                            modelEffort: form.modelEffort || null,
                            description: form.description,
                            system: form.system,
                            agents: cleanRoster(),
                            organization: form.organization || null,
                            enableDashboards: form.enableDashboards,
                            builtinTools: form.tools,
                            skills: skillsPayload(),
                            // Use THIS coordinator's own Key Vault secret — never
                            // a shared default — so the update lands on the
                            // right Anthropic account.
                            apiKeySecret: form.apiKeySecret || undefined
                        })
                    }
                );
                if (!res.ok) {
                    let detail = 'Failed to save coordinator config';
                    try {
                        const j = await res.json();
                        detail = j?.detail || detail;
                    } catch {
                        /* ignore */
                    }
                    throw new Error(detail);
                }
            } else {
                const res = await fetch('/api/agents/coordinator/create', {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        name: form.name,
                        modelId: form.modelId,
                        modelSpeed: form.modelSpeed || null,
                        modelEffort: form.modelEffort || null,
                        description: form.description,
                        system: form.system,
                        agents: cleanRoster(),
                        organization: form.organization || null,
                        enableDashboards: form.enableDashboards,
                        builtinTools: form.tools,
                        skills: skillsPayload(),
                        apiKeySecret: form.apiKeySecret || undefined
                    })
                });
                if (!res.ok) {
                    let detail = 'Failed to create coordinator agent';
                    try {
                        const j = await res.json();
                        detail = j?.detail || detail;
                    } catch {
                        /* ignore */
                    }
                    throw new Error(detail);
                }
                const created: { agentId: string; version: number } =
                    await res.json();
                resolvedAgentId = created.agentId;
            }

            // 2. Upsert the mgmt DB pointer record with isCoordinator=true —
            // this is what the chat runtime and this tab's own list actually
            // rely on to find the coordinator. The mgmt API rejects a second
            // coordinator for the same org with a 400, which we surface
            // as-is rather than a generic failure message.
            const dbConfig: ManagedAgentRequestModel = {
                displayName: form.name,
                organization: form.organization,
                agentId: resolvedAgentId,
                environmentId: form.environmentId,
                vaultId: form.vaultId || undefined,
                isCoordinator: true
            };
            if (editingExisting && form.guid) {
                await mgmtApi.api.v1ManagedAgentsUpdate(form.guid, dbConfig);
            } else {
                await mgmtApi.api.v1ManagedAgentsCreate(dbConfig);
            }

            resetForm();
            fetchCoordinators();
            toast.success('Coordinator config saved!');
        } catch (e: any) {
            toast.error(extractMgmtError(e, 'Failed to save coordinator'));
        } finally {
            setSaving(false);
        }
    };

    const textField = (
        key: 'agentId' | 'organization' | 'modelId' | 'modelSpeed' | 'name',
        label: string,
        placeholder: string,
        opts: { required?: boolean; mono?: boolean; readOnly?: boolean } = {}
    ) => (
        <div className="space-y-2">
            <div className="flex items-center h-5">
                <Label htmlFor={key}>
                    {label}
                    {opts.required && <span className="text-destructive"> *</span>}
                </Label>
            </div>
            <Input
                id={key}
                value={form[key]}
                onChange={(e) =>
                    setForm((f) => ({ ...f, [key]: e.target.value }))
                }
                placeholder={placeholder}
                readOnly={opts.readOnly}
                className={cn(
                    'h-10',
                    opts.mono && 'font-mono',
                    opts.readOnly &&
                        'bg-muted/50 text-muted-foreground cursor-not-allowed'
                )}
            />
        </div>
    );

    // Real per-model capability, not a fixed list — e.g. Haiku doesn't
    // support effort at all, and it 400s if you try to set it.
    const selectedModel = models.find((m) => m.id === form.modelId);
    const effortLevels = selectedModel?.effort_levels ?? [];
    const supportsEffort = !!selectedModel?.supports_effort;

    return (
        <section className="space-y-6">
            {!showForm && (
                <SectionHeader
                    title="Coordinators"
                    level="section"
                    description="Edit coordinator agents' config (name, model, system prompt) directly on Anthropic."
                    action={
                        !loading &&
                        !myOrgCoordinator && (
                            <Button
                                onClick={openNewForm}
                                variant="tertiary"
                            >
                                <Plus />
                                New Coordinator
                            </Button>
                        )
                    }
                    className="items-end mb-6"
                />
            )}

            {/* Header (title only) — flows with the page, same as
                    AgentsTab.tsx's editing header, rather than staying
                    pinned above its own scroll pane. */}
            {!loading && showForm && (
                <SectionHeader
                    title={
                        editingExisting
                            ? 'Edit coordinator'
                            : 'New coordinator'
                    }
                    description="Name, model, system prompt, tools and skills for the coordinator agent."
                />
            )}

            {loading && <TableSkeleton columns={4} />}

            {/* Body — list or form, whichever is active, flowing
                    directly below the header instead of scrolling in its
                    own pane, so the whole tab (header included) scrolls
                    together as one page. */}
            {!loading && !showForm && (
                <div className="space-y-4">
                    {coordinators.length === 0 && (
                        <EmptyState
                            icon={Network}
                            title="No coordinators found"
                            description="Coordinators registered in the database (IsCoordinator=true) show up here"
                        />
                    )}

                    {coordinators.length > 0 && (
                        <div>
                            <Table variant="fluid">
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Name</TableHead>
                                        <TableHead>Version</TableHead>
                                        <TableHead>Status</TableHead>
                                        <TableHead className="w-px whitespace-nowrap">
                                            Actions
                                        </TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {coordinators.map((coord) => (
                                        <TableRow key={coord.guid}>
                                            <TableCell className="font-medium">
                                                {coord.loadError ? (
                                                    <span className="text-amber-600 dark:text-amber-400">
                                                        {coord.displayName}{' '}
                                                        (could not load live
                                                        data)
                                                    </span>
                                                ) : (
                                                    coord.liveName ||
                                                    coord.displayName
                                                )}
                                            </TableCell>
                                            <TableCell className="text-sm">
                                                {coord.liveVersion != null
                                                    ? `v${coord.liveVersion}`
                                                    : '—'}
                                            </TableCell>
                                            <TableCell>
                                                {coord.isComplete ? (
                                                    <StatusBadge tone="success">Active</StatusBadge>
                                                ) : (
                                                    <StatusBadge tone="warning">Missing roster</StatusBadge>
                                                )}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <Button
                                                    variant="tertiary"
                                                    size="default"
                                                    onClick={() =>
                                                        handleEditClick(coord)
                                                    }
                                                    className="gap-2 text-sm"
                                                >
                                                    <Pencil />
                                                    Edit
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </div>
            )}

            {!loading && showForm && (
                <div className="space-y-7">
                    <div className="gap-5 grid grid-cols-1">
                        {textField(
                            'name',
                            'Name',
                            'Orchestration Coordinator',
                            { required: true }
                        )}
                        {/* Agent ID is resolved automatically: for a new
                                        coordinator, POST /api/agents creates the
                                        Anthropic agent and returns its id; for an
                                        existing one it's already resolved from the
                                        DB. Either way it's an internal detail with
                                        no reason to show the admin. */}
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="description">Description</Label>
                        <Textarea
                            id="description"
                            value={form.description}
                            onChange={(e) =>
                                setForm((f) => ({
                                    ...f,
                                    description: e.target.value
                                }))
                            }
                            placeholder="A coordinator agent responsible for ..."
                            className="min-h-18 overflow-visible"
                            spellCheck={false}
                        />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="system">System prompt</Label>
                        <Textarea
                            id="system"
                            value={form.system}
                            onChange={(e) =>
                                setForm((f) => ({
                                    ...f,
                                    system: e.target.value
                                }))
                            }
                            placeholder="You are an orchestration coordinator ..."
                            className="min-h-72"
                            spellCheck={false}
                        />
                    </div>

                    {conflictingCoordinator && (
                        <p className="text-red-600 dark:text-red-400 text-xs">
                            '{conflictingCoordinator.displayName}' is already
                            the coordinator for '{form.organization}'. Only one
                            coordinator is allowed per organization — remove or
                            edit it instead.
                        </p>
                    )}

                    <div className="gap-5 grid grid-cols-1 sm:grid-cols-2">
                        <div className="space-y-2">
                            <Label>
                                Environment
                                <span className="text-destructive"> *</span>
                            </Label>
                            <Select
                                value={form.environmentId || undefined}
                                onValueChange={(value) =>
                                    setForm((f) => ({
                                        ...f,
                                        environmentId: value
                                    }))
                                }
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Select environment" />
                                </SelectTrigger>
                                <SelectContent>
                                    {environments.map((env) => (
                                        <SelectItem key={env.id} value={env.id}>
                                            {env.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label>
                                Vault
                                <span className="text-destructive"> *</span>
                            </Label>
                            <Select
                                value={form.vaultId || undefined}
                                onValueChange={(value) =>
                                    setForm((f) => ({
                                        ...f,
                                        vaultId: value
                                    }))
                                }
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Select vault" />
                                </SelectTrigger>
                                <SelectContent>
                                    {vaults.map((v) => (
                                        <SelectItem key={v.id} value={v.id}>
                                            {v.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    <div className="gap-5 grid grid-cols-1 sm:grid-cols-[1fr_12rem]">
                    <div className="space-y-2">
                        <Label>
                            Model
                            <span className="text-destructive"> *</span>
                        </Label>
                        <Popover
                            open={modelDropdownOpen}
                            onOpenChange={setModelDropdownOpen}
                        >
                            <PopoverTrigger asChild>
                                <Button
                                    variant="tertiary"
                                    role="combobox"
                                    aria-expanded={modelDropdownOpen}
                                    className="justify-between w-full h-10 font-normal text-sm"
                                >
                                    {form.modelId ||
                                        (models.length === 0
                                            ? 'Loading models...'
                                            : 'Select model...')}
                                    <ChevronsUpDown className="opacity-50 ml-1 shrink-0" />
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent
                                className="bg-background! p-0 w-(--radix-popover-trigger-width)"
                                align="start"
                            >
                                <Command className="bg-background!">
                                    <CommandInput placeholder="Search models..." />
                                    <CommandList className="max-h-64">
                                        <CommandEmpty>
                                            No models found.
                                        </CommandEmpty>
                                        <CommandGroup>
                                            {models.map((m) => (
                                                <CommandItem
                                                    key={m.id}
                                                    value={`${m.id} ${m.display_name}`}
                                                    onSelect={() => {
                                                        setForm((f) => ({
                                                            ...f,
                                                            modelId: m.id,
                                                            // Drop an effort level the newly
                                                            // picked model doesn't actually
                                                            // support — carrying it over would
                                                            // save fine locally but 400 against
                                                            // the real Anthropic API.
                                                            modelEffort:
                                                                f.modelEffort &&
                                                                m.effort_levels?.includes(
                                                                    f.modelEffort
                                                                )
                                                                    ? f.modelEffort
                                                                    : ''
                                                        }));
                                                        setModelDropdownOpen(
                                                            false
                                                        );
                                                    }}
                                                >
                                                    <Check
                                                        className={cn(
                                                            'mr-2 size-4 shrink-0',
                                                            form.modelId ===
                                                                m.id
                                                                ? 'opacity-100'
                                                                : 'opacity-0'
                                                        )}
                                                    />
                                                    <span className="truncate">
                                                        {m.id}
                                                    </span>
                                                </CommandItem>
                                            ))}
                                        </CommandGroup>
                                    </CommandList>
                                </Command>
                            </PopoverContent>
                        </Popover>
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="coordinator-effort-select">Effort</Label>
                                    <Select
                                        disabled={!supportsEffort}
                                        value={
                                            supportsEffort
                                                ? form.modelEffort || 'default'
                                                : 'unsupported'
                                        }
                                        onValueChange={(value) =>
                                            setForm((f) => ({
                                                ...f,
                                                modelEffort:
                                                    value === 'default'
                                                        ? ''
                                                        : (value as CoordinatorForm['modelEffort'])
                                            }))
                                        }
                                    >
                                        <SelectTrigger id="coordinator-effort-select">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {supportsEffort ? (
                                                <>
                                                    <SelectItem value="default">
                                                        Default
                                                    </SelectItem>
                                                    {effortLevels.map(
                                                        (level) => (
                                                            <SelectItem
                                                                key={level}
                                                                value={level}
                                                            >
                                                                {EFFORT_LEVEL_LABELS[
                                                                    level
                                                                ] ?? level}
                                                            </SelectItem>
                                                        )
                                                    )}
                                                </>
                                            ) : (
                                                <SelectItem value="unsupported">
                                                    Not supported
                                                </SelectItem>
                                            )}
                                        </SelectContent>
                                    </Select>
                    </div>
                    </div>

                    <ToggleSection title="Built-in tools">
                        {BUILTIN_TOOLS.map((tool) => {
                            const state = form.tools[tool.name];
                            return (
                                <ToggleRow
                                    key={tool.name}
                                    id={`tool-${tool.name}`}
                                    title={tool.name}
                                    description={tool.description}
                                    checked={state.enabled}
                                    onCheckedChange={(checked) =>
                                        setTool(tool.name, { enabled: checked })
                                    }
                                    extra={
                                        state.enabled && (
                                            <AutoConfirmControl
                                                auto={state.auto}
                                                onChange={(auto) =>
                                                    setTool(tool.name, { auto })
                                                }
                                            />
                                        )
                                    }
                                />
                            );
                        })}
                    </ToggleSection>

                    <ToggleSection title="Dashboards">
                        <ToggleRow
                            id="coordinator-dashboards"
                            title="Dashboards"
                            description="Let this coordinator build and save dashboards for the user with the create_dashboard tool."
                            checked={form.enableDashboards}
                            onCheckedChange={(checked) =>
                                setForm((f) => ({ ...f, enableDashboards: checked }))
                            }
                        />
                    </ToggleSection>

                    {/* MCP servers — mcp_apps (chart rendering) is the only
                            MCP server a coordinator gets; it's attached
                            automatically server-side (see _merge_mcp_apps in
                            coordinator_agents.py), so it's shown here purely
                            as a status indicator, locked "on". Coordinators
                            delegate data lookups to roster sub-agents rather
                            than querying data MCP servers themselves, so
                            there's no picker for those here (unlike
                            AgentsTab). */}
                    {mcpCatalog.some((e) => e.name === 'mcp_apps') && (
                        <ToggleSection title="MCP servers">
                            <ToggleRow
                                id="mcp-apps"
                                title={
                                    mcpCatalog.find((e) => e.name === 'mcp_apps')
                                        ?.displayName || 'MCP Apps'
                                }
                                description="Always enabled for coordinators"
                                checked
                                disabled
                            />
                        </ToggleSection>
                    )}

                    <div className="space-y-3">
                        <SkillToggleRows
                            skills={skillCatalog}
                            selectedIds={form.selectedSkills}
                            onToggle={toggleSkill}
                            deletingSkillId={deletingSkillId}
                            skillToDelete={skillToDelete}
                            onSkillToDeleteChange={setSkillToDelete}
                            onDelete={handleDeleteSkill}
                        />

                        <div
                            {...getSkillDropRootProps()}
                            className={cn(
                                'flex items-center gap-2.5 p-4 border border-dashed rounded-lg text-sm cursor-pointer transition-colors',
                                isSkillDropActive
                                    ? 'border-foreground bg-foreground/5'
                                    : 'border-muted-foreground/20 hover:border-foreground/40',
                                uploadingSkill &&
                                    'opacity-60 pointer-events-none'
                            )}
                        >
                            <input {...getSkillDropInputProps()} />
                            {uploadingSkill ? (
                                <Loader2 className="size-4 animate-spin" />
                            ) : (
                                <UploadCloud className="size-4" />
                            )}
                            <span className="text-muted-foreground">
                                Click to{' '}
                                <span className="text-foreground underline underline-offset-2">
                                    upload a custom skill
                                </span>{' '}
                                (.skill or .zip, with a SKILL.md at its root)
                            </span>
                        </div>
                    </div>

                    <div className="flex justify-end items-center gap-3 pt-4 border-border border-t">
                        <Button onClick={resetForm} variant="tertiary">
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSave}
                            disabled={saving || !!conflictingCoordinator}
                        >
                            {saving ? (
                                <>
                                    <Loader2 className="mr-2 animate-spin" />
                                    Saving...
                                </>
                            ) : (
                                'Save Changes'
                            )}
                        </Button>
                    </div>
                </div>
            )}
        </section>
    );
};
